import type { NotificationReaderContext } from '@/modules/notifications/reads.api'
import { queryKeys } from '@/hooks/query-keys'
import { notificationCommandsApi } from '@/modules/notifications/commands.api'

import { useNotificationReadSession } from './useNotificationReadSession'
import { useOwnedReaderMutation } from './useOwnedReaderMutation'

function useNotificationCommand<Input, Result>(
  execute: (
    input: Input,
    context: NotificationReaderContext,
  ) => Promise<Result>,
  successMessage?: string | ((result: Result) => string),
) {
  return useOwnedReaderMutation(useNotificationReadSession(), {
    execute,
    queryRoot: queryKeys.notifications.all,
    singleFlight: true,
    busyMessage: 'Notification change is already in progress',
    successMessage,
    errorMessage:
      'The notification change could not be confirmed. Refresh before trying again.',
  })
}
export function useNotificationRead() {
  return useNotificationCommand(notificationCommandsApi.read)
}
export function useNotificationsRead() {
  return useNotificationCommand(
    notificationCommandsApi.readMany,
    result =>
      `Marked ${result.updated} notification${result.updated === 1 ? '' : 's'} as read`,
  )
}
export function useAllNotificationsRead() {
  return useNotificationCommand(
    notificationCommandsApi.readAll,
    result =>
      `Marked ${result.updated} notification${result.updated === 1 ? '' : 's'} as read`,
  )
}
export function useNotificationDismiss() {
  return useNotificationCommand(
    notificationCommandsApi.dismiss,
    'Notification dismissed',
  )
}
export function useNotificationsDismiss() {
  return useNotificationCommand(
    notificationCommandsApi.dismissMany,
    result =>
      `Dismissed ${result.updated} notification${result.updated === 1 ? '' : 's'}`,
  )
}
export function useAllNotificationsDismiss() {
  const mutation = useNotificationCommand(
    notificationCommandsApi.dismissAll,
    result =>
      `Dismissed ${result.dismissed} notification${result.dismissed === 1 ? '' : 's'}`,
  )
  type Callbacks = Parameters<typeof mutation.mutate>[1]
  return {
    ...mutation,
    mutate: (types?: string[], callbacks?: Callbacks) =>
      mutation.mutate(types, callbacks),
    mutateAsync: (types?: string[], callbacks?: Callbacks) =>
      mutation.mutateAsync(types, callbacks),
  }
}
export function useNotificationPreferenceUpdate() {
  return useNotificationCommand(
    notificationCommandsApi.preference,
    'Notification preference updated',
  )
}
export function useNotificationPreferencesUpdate() {
  return useNotificationCommand(
    notificationCommandsApi.preferences,
    'Notification preferences updated',
  )
}
export function useNotificationPreferencesReset() {
  return useNotificationCommand(
    notificationCommandsApi.defaults,
    'Default preferences restored',
  )
}
